import React, { useState, useRef, useEffect } from "react";
import RecipientsDropdown from "./RecipientsDropdown";
import TextEditor from "../TextEditor";
import Screens from "./Screens";
import { FaPlus } from "react-icons/fa";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import translations from "../../translation"; // Import translations

const Form = ({ language }) => {
  const [logo, setLogo] = useState(null);
  const [selectedOption, setSelectedOption] = useState("Manual"); // Default option
  const [manualData, setManualData] = useState(""); // State for manual data
  const [fileData, setFileData] = useState(null); // State for file data
  const [companyData, setCompanyData] = useState(""); // State for Company data
  const [attachment, setAttachment] = useState(null); // State for attachment
  const [title, setTitle] = useState(""); // State for title
  const [name, setName] = useState(""); // State for name
  const [loading, setLoading] = useState(false); // Loading state for submit button
  const [userListing, setuserListing] = useState(); // Loading state for submit button
  const [content, setContent] = useState({ text: "", attachment: null, direction: "" });
  const [approvals, setApprovals] = useState([]); // State to store approval data
  const [selectedApproval, setSelectedApproval] = useState(""); // State to store selected dropdown value
  const [showDropdown, setShowDropdown] = useState(false); // State to manage dropdown visibility

  // Translate function for static text
  const translate = (key) => translations[language][key] || key;

  useEffect(() => {
    getAllUsers();
    const currentPath = window.location.pathname;
    if (currentPath.includes("user/") || currentPath.includes("approver/")) {
      setShowDropdown(true); // Show dropdown if condition is met
    }
  }, []);

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogo(file); // Store raw file directly
    }
  };

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };
  const validateFileType = (file) => {
    const allowedExtensions = ["csv", "xls", "xlsx"];
    const fileExtension = file.name.split(".").pop().toLowerCase();
    return allowedExtensions.includes(fileExtension);
  };
  const getAllUsers = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_CHECK_USER_API}/api/approval/list`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        toastr.error("Failed to fetch user listing.");
        return;
      }

      const data = await response.json();

      if (data.success) {
        setuserListing(data.data || []);
      } else {
        toastr.warning("No users found in the listing.");
      }
    } catch (error) {
      console.error("Error fetching user listing:", error.message);
      toastr.error("An error occurred while fetching user listing.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const user = JSON.parse(localStorage.getItem("user"));

    if (!user) {
      console.error("User not found in localStorage.");
      return;
    }

    setLoading(true); // Start loading

    const formData = new FormData();
    formData.append("sender_id", user.id);
    formData.append("title", title);
    formData.append("name", name);
    formData.append("direction", content.direction || "ltr");
    formData.append("content", content.text || "");
    formData.append("method", selectedOption);

    // Add logo if it exists
    if (logo) {
      formData.append("logo", logo);
    }

    // Add attachment if it exists
    if (content.attachment) {
      formData.append("attachment", content.attachment);
    }

    // Add data based on selected option
    if (selectedOption === "Manual") {
      formData.append("manual_data", manualData);
    } else if (selectedOption === "File") {
      if (fileData) {
        if (!validateFileType(fileData)) {
          toastr.error(translate("invalidFileType"));
          setLoading(false);
          return;
        }
        formData.append("file", fileData);
      }

    } else if (selectedOption === "Company") {
      formData.append("company_data", companyData);
    }
    if (selectedApproval && selectedApproval.length > 0) {
      formData.append("approvals", selectedApproval);
    }
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_CHECK_USER_API}/api/user/announcement`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        toastr.error(translate("formSubmitError"));
        setLoading(false);
        return;
      }

      const data = await response.json();
      toastr.success(translate("formSubmitSuccess"));
    } catch (error) {
      toastr.error(error.message);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  useEffect(() => {
    // Fetch approval data only if dropdown should be shown
    const fetchApprovals = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_CHECK_USER_API}/api/approval/list`,
          {
            method: "GET",
          }
        );

        if (!response.ok) {
          toastr.error("Failed to fetch user listing.");
          return;
        }

        const data = await response.json();

        if (data.success) {
          setApprovals(data.data || []);
        } else {
          toastr.warning("No users found in the listing.");
        }
      } catch (error) {
        console.error("Error fetching user listing:", error.message);
        toastr.error("An error occurred while fetching user listing.");
      }
    };

    fetchApprovals();
  }, [showDropdown]);

  return (
    <div className="min-h-screen bg-light d-flex flex-column justify-content-center py-5">
      <div className="container">
        <div className="row">
          <div className="col-lg-8">
            <div className="text-center mb-4">
              <h2 className="h4 font-weight-bold">{translate("formTitle")}</h2>
              <p className="text-muted">{translate("formSubtitle")}</p>
            </div>
            <div className="bg-white shadow p-4 rounded">
              <form onSubmit={handleSubmit}>
                {/* Title */}
                <div className="mb-3">
                  <label className="form-label">{translate("titleLabel")}</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={translate("titlePlaceholder")}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    style={{ textTransform: "capitalize" }}
                  />
                </div>

                {/* Name and Email */}
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">{translate("nameLabel")}</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={translate("namePlaceholder")}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      style={{ textTransform: "capitalize" }}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">{translate("emailLabel")}</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder={translate("emailPlaceholder")}
                      required
                    />
                  </div>
                </div>
                {showDropdown && (
                  <div className="mb-3">
                    <label className="form-label">{translate("selectApprover")}</label>
                    <select
                      className="form-control"
                      value={selectedApproval}
                      onChange={(e) => setSelectedApproval(e.target.value)}
                      required
                    >
                      <option value="" disabled>
                      {translate("selectApprover")}
                      </option>
                      {approvals.map((approval) => (
                        <option key={approval.id} value={approval.id}>
                          {approval.fullname}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {/* Logo Upload */}
                <div className="row border border-custom rounded mx-1">
                  <div className="col-md-6 mb-3 d-flex align-items-center justify-content-center">
                    <label className="form-label block text-md font-medium text-gray-700 me-4">
                      {translate("logoLabel")}
                    </label>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => document.getElementById("logo-upload").click()}
                    >
                      {translate("uploadLogoButton")}
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      id="logo-upload"
                      className="d-none"
                      onChange={handleLogoUpload}
                    />
                  </div>
                  <div className="col-md-2 mb-3 mt-2">
                    <div
                      className="d-flex justify-content-center align-items-center border border-dashed rounded bg-light p-1"
                      style={{ width: "100%", height: "100px" }}
                    >
                      {logo ? (
                        <img
                          src={logo}
                          alt="Uploaded Logo"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "0.375rem",
                          }}
                        />
                      ) : (
                        <FaPlus />
                      )}
                    </div>
                  </div>
                </div>

                {/* Radio Buttons */}
                <div className="row">
                  <div className="col-12 my-3 custom-radio-group">
                    <label className="custom-radio-label block text-sm font-medium text-gray-700 mb-2 mt-3">
                      {translate("radioLabel")}
                    </label>
                    <div className="d-flex custom-radio-buttons">
                      <div className="custom-radio-option me-3">
                        <label
                          htmlFor="custom-option1"
                          className="d-flex align-items-center"
                        >
                          <input
                            type="radio"
                            name="custom-options"
                            id="custom-option1"
                            value="Manual"
                            checked={selectedOption === "Manual"}
                            onChange={handleOptionChange}
                          />
                          <span className="ms-1">{translate("manualOption")}</span>
                        </label>
                      </div>
                      <div className="custom-radio-option me-3">
                        <label
                          htmlFor="custom-option2"
                          className="d-flex align-items-center"
                        >
                          <input
                            type="radio"
                            name="custom-options"
                            id="custom-option2"
                            value="File"
                            checked={selectedOption === "File"}
                            onChange={handleOptionChange}
                          />
                          <span className="ms-1">{translate("fileOption")}</span>
                        </label>
                      </div>
                      <div className="custom-radio-option">
                        <label
                          htmlFor="custom-option3"
                          className="d-flex align-items-center"
                        >
                          <input
                            type="radio"
                            name="custom-options"
                            id="custom-option3"
                            value="Company"
                            checked={selectedOption === "Company"}
                            onChange={handleOptionChange}
                          />
                          <span className="ms-1">{translate("companyOption")}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dynamic Content */}
                <div className="row">
                  <div className="col-12">
                    {selectedOption === "Manual" && (
                      <div className="mb-3">
                        <label className="form-label">{translate("manualInputLabel")}</label>
                        <br />
                        <small className="text-danger">{translate("manualInputHint")}</small>
                        <input
                          type="text"
                          className="form-control"
                          placeholder={translate("manualInputPlaceholder")}
                          value={manualData}
                          onChange={(e) => setManualData(e.target.value)}
                          required
                        />
                      </div>
                    )}

                    {selectedOption === "File" && (
                      <div className="mb-3">
                        <label className="form-label">{translate("fileInputLabel")}</label>
                        <input
                          type="file"
                          className="form-control"
                          onChange={(e) => setFileData(e.target.files[0])}
                          required
                        />
                      </div>
                    )}

                    {selectedOption === "Company" && (
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {translate("companySelectLabel")}
                        </label>
                        <div className="relative">
                          <select
                            value={companyData}
                            onChange={(e) => setCompanyData(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="" disabled>
                              {translate("companySelectPlaceholder")}
                            </option>
                            <option value="Sanabel All Employees">{translate("sanabelOption")}</option>
                            <option value="Al Naghi All Employees">{translate("alNaghiOption")}</option>
                            <option value="Mosanada IT Staff">{translate("mosanadaOption")}</option>
                          </select>
                        </div>
                        <div className="mt-2">
                          {companyData && (
                            <p className="text-sm text-gray-700">
                              <strong>{translate("selectedGroupLabel")}:</strong> {companyData}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* TextEditor */}
                <div className="my-3">
                  <TextEditor setContent={setContent} setAttachment={setAttachment} language={language} />
                </div>

                {/* Action Buttons */}
                <div className="d-flex justify-content-end mt-4">
                  <button type="reset" className="btn btn-outline-danger me-3">
                    {translate("resetButton")}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={loading} // Disable button during loading
                  >
                    {loading ? translate("submitting") : translate("submitButton")}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Screens Component */}
          <div className="col-lg-4">
            <div className="text-center mb-4">
              <h2 className="h4 font-weight-bold">{translate("previewTitle")}</h2>
              <p className="text-muted">{translate("previewSubtitle")}</p>
            </div>
            <Screens content={content.text} title={title} name={name} language={language} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Form;
